import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import i18next from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';

import App from '@/App';
import { AppErrorBoundary } from '@/components/Ui/AppErrorBoundary';
import { Loader } from '@/components/Ui/Loader';
import { Toaster as Sonner } from '@/components/Ui/Sonner';
import auth from '@/helpers/auth';
import { registerGlobalErrorReporting, reportXhrError } from '@/helpers/clientError';
import { getToken, refreshToken } from '@/helpers/clientToken';
import { type Config, set as registerConfig } from '@/helpers/config';
import { notifyImpersonationBlocked } from '@/helpers/impersonation';
import pages_de from '@/translations/de/general.json';
import pages_en from '@/translations/en/general.json';
import pages_es from '@/translations/es/general.json';
import pages_fr from '@/translations/fr/general.json';
import pages_it from '@/translations/it/general.json';

i18next.init({
    interpolation: { escapeValue: false }, // React already does escaping
    lng: auth.bootstrapLocale(), // Browser language until a user logs in
    fallbackLng: 'en',
    resources: {
        en: {
            pages: pages_en, // 'pages' is our custom namespace
        },
        es: {
            pages: pages_es, // 'pages' is our custom namespace
        },
        de: {
            pages: pages_de, // 'pages' is our custom namespace
        },
        fr: {
            pages: pages_fr, // 'pages' is our custom namespace
        },
        it: {
            pages: pages_it, // 'pages' is our custom namespace
        },
    },
});

const Render = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [
        profile,
        setProfile,
    ] = useState<Record<string, unknown> | null | false>(null);

    const [
        config,
        setConfig,
    ] = useState<Config | null>(null);

    const navigateRef = useRef(navigate);

    useEffect(() => {
        navigateRef.current = navigate;
    });

    useEffect(() => {
        const requestId = axios.interceptors.request.use(
            (config) => {
                let token = getToken();

                if (!token) {
                    token = refreshToken();
                }

                config.headers['client-application-token'] = token;

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        const responseId = axios.interceptors.response.use((response) => {
            return response;
        }, (error) => {
            if (auth.isIdentified() && error?.response?.status === 401) {
                setProfile(false);
                auth.clearSession();
                navigateRef.current('/refresh-session');

                return;
            }

            return Promise.reject(error);
        });

        return () => {
            axios.interceptors.request.eject(requestId);
            axios.interceptors.response.eject(responseId);
        };
    }, []);

    const fetchProfile = useCallback(async (signal: AbortSignal) => {
        await axios.get(`/api/profile`, { signal }).then(({ data }) => {
            setProfile(data);
            auth.setUser(data);
            auth.setDeeplink(null);
        }).catch(function (e) {
            if (!axios.isCancel(e)) {
                setProfile(false);
            }
        });
    }, []);

    const fetchConfig = useCallback(async (signal: AbortSignal) => {
        await axios.get(`/api/config`, { signal }).then(({ data }) => {
            registerConfig(data);
            setConfig(data);
        }).catch(function (e) {
            if (!axios.isCancel(e)) {
                throw e;
            }
        });
    }, []);

    useEffect(() => {
        auth.setDeeplink(location.pathname);
    }, [
        location.pathname,
    ]);

    useEffect(() => {
        const controller = new AbortController();

        fetchConfig(controller.signal);
        fetchProfile(controller.signal);

        return () => {
            return controller.abort();
        };
    }, [
        fetchConfig,
        fetchProfile,
    ]);

    // Re-fetch the application config when the session context changes
    // (e.g. an administrator entering or leaving a customer workspace).
    // Feature flags are per-customer, so the config payload changes too.
    useEffect(() => {
        const controller = new AbortController();

        const handleSessionChanged = () => {
            fetchConfig(controller.signal);
        };

        window.addEventListener('session-changed', handleSessionChanged);

        return () => {
            window.removeEventListener('session-changed', handleSessionChanged);
            controller.abort();
        };
    }, [
        fetchConfig,
    ]);

    useEffect(() => {
        if (profile && auth.isIdentified() && auth.is2faDisabled()) {
            navigate('/connect-2fa');
        }
    }, [
        profile,
        navigate,
    ]);

    useEffect(() => {
        document.title = `Felice Headwear - ${ location.pathname }`;
    }, [
        location,
    ]);

    if (profile === null || config === null) {
        return <Loader />;
    }

    return <App />;
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 0,
        },
    },
    mutationCache: new MutationCache({
        onError: (error) => {
            notifyImpersonationBlocked(error);
            reportXhrError(error);
        },
    }),
    queryCache: new QueryCache({ onError: reportXhrError }),
});

registerGlobalErrorReporting();

if (document.getElementById('root')) {
    const Index = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

    Index.render(
        <React.StrictMode>
            <QueryClientProvider client={ queryClient }>
                <Router>
                    <Sonner closeButton duration={ 3000 } richColors/>

                    <I18nextProvider i18n={ i18next }>
                        <AppErrorBoundary>
                            <Render />
                        </AppErrorBoundary>
                    </I18nextProvider>
                </Router>
            </QueryClientProvider>
        </React.StrictMode>
    );
}
